import * as React from 'react';
import { DEFAULT_TILE_PALETTE, SurfaceType, TileType, TilePaletteItem } from '../../../../shared/TileEditorTypes';

interface TilePaletteProps {
  currentTile: TileType;
  onTileSelect: (tileType: TileType) => void;
  brushSize: number;
  onBrushSizeChange: (size: number) => void;
  hasSurface?: boolean;
  surfaceType?: SurfaceType;
  onSurfaceToggle?: (enabled: boolean) => void;
  onSurfaceTypeChange?: (surfaceType: SurfaceType) => void;
}

// Icon mapping for ALL tile types (every category is reachable via tabs).
const TILE_ICONS: Record<number, string> = {
  // ground
  [TileType.FLOOR]: '🟫',
  [TileType.STONE]: '🪨',
  [TileType.WOOD]: '🪵',
  [TileType.WATER]: '💧',
  [TileType.LAVA]: '🔥',
  [TileType.ICE]: '🧊',
  [TileType.ROPE]: '🪢',
  [TileType.GRASS]: '🌱',
  // collision
  [TileType.WALL]: '🧱',
  [TileType.DOOR]: '🚪',
  [TileType.WINDOW]: '🪟',
  [TileType.HALF_WALL]: '🔲',
  // objects
  [TileType.CHEST]: '🎁',
  [TileType.LEVER]: '🎚️',
  // entities
  [TileType.NPC_SPAWN]: '🧍',
  [TileType.TRAINING_DUMMY]: '🎯',
  [TileType.SPAWN_POINT]: '🏁',
  [TileType.TELEPORTER]: '🌀',
  // triggers
  [TileType.TRAP]: '⚠️',
  [TileType.SAFE_ZONE]: '🛡️',
  [TileType.EXIT_ZONE]: '🚩',
};

type TileCategory = TilePaletteItem['category'];

// Category tab metadata — order + friendly label/icon. The 5 categories
// match `DEFAULT_TILE_PALETTE` so every one of the 21 tile types is reachable
// (previously only the 8 `ground` tiles were exposed).
const TILE_CATEGORIES: Array<{ id: TileCategory; label: string; icon: string }> = [
  { id: 'ground', label: 'Ground', icon: '🎨' },
  { id: 'collision', label: 'Walls', icon: '🧱' },
  { id: 'objects', label: 'Objects', icon: '📦' },
  { id: 'entities', label: 'Spawns', icon: '🎯' },
  { id: 'triggers', label: 'Triggers', icon: '⚡' },
];

// All palette tiles with icons, grouped once by category.
const TILES_WITH_ICONS: Array<TilePaletteItem & { icon: string }> = DEFAULT_TILE_PALETTE.map(tile => ({
  ...tile,
  icon: TILE_ICONS[tile.type] || '⬜',
}));

const TILES_BY_CATEGORY: Record<TileCategory, Array<TilePaletteItem & { icon: string }>> =
  TILE_CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = TILES_WITH_ICONS.filter(t => t.category === cat.id);
    return acc;
  }, {} as Record<TileCategory, Array<TilePaletteItem & { icon: string }>>);

/** Look up any palette tile by type (across all categories). */
const findTile = (type: TileType) => TILES_WITH_ICONS.find(t => t.type === type);

// Secondary surface overlays (top 25% layer)
const SURFACE_TYPES = [
  { type: SurfaceType.NONE, name: 'None', color: 'transparent', icon: '❌' },
  { type: SurfaceType.GRASS, name: 'Grass', color: '#58D68D', icon: '🌿' },
  { type: SurfaceType.SAND, name: 'Sand', color: '#F4D03F', icon: '🏖️' },
  { type: SurfaceType.SNOW, name: 'Snow', color: '#FFFFFF', icon: '❄️' },
  { type: SurfaceType.MOSS, name: 'Moss', color: '#7D8471', icon: '🍃' },
  { type: SurfaceType.WATER_SHALLOW, name: 'Shallow', color: '#5DADE2', icon: '💦' },
  { type: SurfaceType.LAVA_CRACKS, name: 'Cracks', color: '#FF5733', icon: '🌋' },
  { type: SurfaceType.FLOWERS, name: 'Flowers', color: '#FF69B4', icon: '🌸' },
  { type: SurfaceType.VINES, name: 'Vines', color: '#228B22', icon: '🌾' },
];

export const TilePalette: React.FC<TilePaletteProps> = ({
  currentTile,
  onTileSelect,
  brushSize,
  onBrushSizeChange,
  hasSurface = false,
  surfaceType = SurfaceType.NONE,
  onSurfaceToggle,
  onSurfaceTypeChange
}) => {
  // Active tile category. Seed from the currently-selected tile so the right
  // tab is shown on mount; defaults to 'ground'.
  const [activeCategory, setActiveCategory] = React.useState<TileCategory>(
    () => findTile(currentTile)?.category ?? 'ground'
  );
  const categoryTiles = TILES_BY_CATEGORY[activeCategory] ?? [];

  return (
    <div style={{
      width: '280px',
      backgroundColor: '#34495e',
      color: 'white',
      padding: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      height: '100%',
      overflowY: 'auto'
    }}>
      <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        🎨 Tile Palette
      </h3>

      {/* Brush Size Controls */}
      <div style={{ backgroundColor: '#2c3e50', padding: '10px', borderRadius: '6px' }}>
        <div style={{ marginBottom: '8px', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '5px' }}>
          🖌️ Brush Size: {brushSize}x{brushSize}
        </div>
        <div style={{ display: 'flex', gap: '5px' }}>
          {[1, 2, 3, 4, 5].map(size => (
            <button
              key={size}
              onClick={() => onBrushSizeChange(size)}
              style={{
                flex: 1,
                padding: '6px',
                backgroundColor: brushSize === size ? '#3498db' : '#2c3e50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              {size}x{size}
            </button>
          ))}
        </div>
      </div>

      {/* CATEGORY TABS — every tile category is reachable */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        {TILE_CATEGORIES.map(cat => {
          const active = activeCategory === cat.id;
          const count = TILES_BY_CATEGORY[cat.id]?.length ?? 0;
          if (count === 0) return null;
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              title={`${cat.label} (${count})`}
              style={{
                flex: '1 0 auto',
                padding: '6px 8px',
                backgroundColor: active ? '#3498db' : '#2c3e50',
                color: 'white',
                border: `1px solid ${active ? '#ffffff' : '#3498db'}`,
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: active ? 'bold' : 'normal',
                whiteSpace: 'nowrap'
              }}
            >
              {cat.icon} {cat.label}
            </button>
          );
        })}
      </div>

      {/* TILES SECTION (filtered by active category) */}
      <div style={{
        backgroundColor: '#2c3e50',
        padding: '12px',
        borderRadius: '6px',
        border: '2px solid #3498db'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 'bold',
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#3498db'
        }}>
          ▣ {TILE_CATEGORIES.find(c => c.id === activeCategory)?.label} Tiles
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '8px'
        }}>
          {categoryTiles.map(tile => (
            <div
              key={tile.type}
              onClick={() => onTileSelect(tile.type)}
              style={{
                backgroundColor: currentTile === tile.type ? '#3498db' : '#34495e',
                border: `2px solid ${currentTile === tile.type ? '#ffffff' : tile.color}`,
                borderRadius: '6px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s',
                position: 'relative'
              }}
              title={tile.name}
            >
              {/* Tile Preview */}
              <div style={{
                width: '36px',
                height: '36px',
                backgroundColor: tile.color,
                borderRadius: '4px',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px'
              }}>
                {tile.icon}
              </div>

              {/* Tile Label */}
              <div style={{
                fontSize: '10px',
                textAlign: 'center',
                fontWeight: currentTile === tile.type ? 'bold' : 'normal'
              }}>
                {tile.name}
              </div>

              {/* Selection Indicator */}
              {currentTile === tile.type && (
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '14px',
                  height: '14px',
                  backgroundColor: '#27ae60',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px'
                }}>
                  ✓
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* SECONDARY SURFACE SECTION — surfaces only make sense on ground tiles */}
      {activeCategory === 'ground' && (
      <div style={{
        backgroundColor: '#2c3e50',
        padding: '12px',
        borderRadius: '6px',
        border: '2px solid #27ae60',
        flex: 1
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 'bold',
          marginBottom: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#27ae60'
        }}>
          <span>⬆️ Surface Overlay (Top 25%)</span>
          
          {/* Surface Toggle */}
          <button
            onClick={() => onSurfaceToggle?.(!hasSurface)}
            style={{
              padding: '4px 10px',
              backgroundColor: hasSurface ? '#27ae60' : '#34495e',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 'bold'
            }}
          >
            {hasSurface ? 'ON' : 'OFF'}
          </button>
        </div>
        
        {/* Surface Type Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '8px',
          opacity: hasSurface ? 1 : 0.5,
          pointerEvents: hasSurface ? 'auto' : 'none'
        }}>
          {SURFACE_TYPES.map(surface => (
            <div
              key={surface.type}
              onClick={() => onSurfaceTypeChange?.(surface.type)}
              style={{
                backgroundColor: surfaceType === surface.type ? '#27ae60' : '#34495e',
                border: `2px solid ${surfaceType === surface.type ? '#ffffff' : (surface.color === 'transparent' ? '#666' : surface.color)}`,
                borderRadius: '6px',
                padding: '8px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s',
                position: 'relative'
              }}
              title={surface.name}
            >
              {/* Surface Preview */}
              <div style={{
                width: '36px',
                height: '36px',
                backgroundColor: surface.color === 'transparent' ? '#34495e' : surface.color,
                borderRadius: '4px',
                border: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Show as top 25% overlay preview */}
                {surface.type !== SurfaceType.NONE && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '25%',
                    backgroundColor: surface.color,
                    opacity: 0.8
                  }} />
                )}
                {surface.icon}
              </div>

              {/* Surface Label */}
              <div style={{
                fontSize: '10px',
                textAlign: 'center',
                fontWeight: surfaceType === surface.type ? 'bold' : 'normal'
              }}>
                {surface.name}
              </div>

              {/* Selection Indicator */}
              {surfaceType === surface.type && hasSurface && (
                <div style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '14px',
                  height: '14px',
                  backgroundColor: '#3498db',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '9px'
                }}>
                  ✓
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{
          marginTop: '10px',
          fontSize: '11px',
          opacity: 0.7,
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          {hasSurface
            ? `Surface renders on top 25% of tile`
            : `Toggle ON to add surface overlay`}
        </div>
      </div>
      )}

      {/* Selected Tile Summary */}
      <div style={{
        backgroundColor: '#1a252f',
        padding: '12px',
        borderRadius: '6px',
        border: '1px solid #34495e'
      }}>
        <div style={{ fontSize: '12px', marginBottom: '8px', opacity: 0.8 }}>
          Selected Combination:
        </div>
        
        {/* Primary + Surface Preview */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Composite Preview */}
          <div style={{
            width: '50px',
            height: '50px',
            borderRadius: '6px',
            border: '2px solid rgba(255,255,255,0.3)',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: findTile(currentTile)?.color || '#666'
          }}>
            {/* Base tile */}
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: findTile(currentTile)?.color || '#666'
            }} />

            {/* Surface overlay (top 25%) */}
            {activeCategory === 'ground' && hasSurface && surfaceType !== SurfaceType.NONE && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '25%',
                backgroundColor: SURFACE_TYPES.find(s => s.type === surfaceType)?.color || 'transparent',
                opacity: 0.9,
                borderBottom: '1px solid rgba(255,255,255,0.3)'
              }} />
            )}
          </div>
          
          {/* Labels */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#3498db' }}>
              {findTile(currentTile)?.name || 'Unknown'}
            </div>
            {activeCategory === 'ground' && hasSurface && surfaceType !== SurfaceType.NONE && (
              <div style={{ fontSize: '12px', color: '#27ae60', marginTop: '2px' }}>
                + {SURFACE_TYPES.find(s => s.type === surfaceType)?.name}
              </div>
            )}
            {activeCategory === 'ground' && !hasSurface && (
              <div style={{ fontSize: '11px', opacity: 0.5, marginTop: '2px' }}>
                No surface overlay
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
